import { SharedHistoryEvents } from '../historyTypes';
import { httpClient } from '../httpClient/httpClient';
import { Sample, SamplesQuery } from './samplesApi';
import { SupplierInquiry } from './inquiryApi';
import { Offer } from './offerApi';

export enum EntityType {
  Sample = 1,
  ProductApproval = 2,
  CompositionGroup = 3,
  Variant = 4,
  Article = 5,
  Concept = 6,
  Customer = 7,
  Supplier = 8,
  Inquiry = 9,
  Offer = 10,
}

export interface SimpleTracker {
  entityId: number;
  trackerId: number;
  entityType: EntityType;
  trackedType: EntityType;
}

export interface AddWatcherRequest {
  entityId: number;
  entityType: EntityType;
  watchEntityType: EntityType;
}

interface Watcher {
  id: number;
  entityId: number;
  userId: number;
  entityType: EntityType;
  watchingEntityType: EntityType;
}

export interface WatcherEntitySummary {
  data: Record<
    EntityType,
    | {
        watcherId: number | null;
        userId: number | null;
        nrOfSubscribedTrackers: number;
        totalTrackers: number;
      }
    | undefined
  >;
}

export interface TrackingSummary {
  pointers: SimpleTracker[];
  watchers: Watcher[];
}

export interface RemoveWatcherResponse {
  failedUnsubscribedIds: number[];
  entityType: EntityType;
}

export interface Change {
  userName: string;
  date: string;
  eventType: SharedHistoryEvents;
}

export interface Trackable {
  changes: Change[];
  isChangedSinceLastTrack: boolean;
}

export const trackerApi = {
  addWatcher: (data: AddWatcherRequest) =>
    httpClient.post(`tracker/watcher`, { data }),
  removeWatcher: (watcherId: number) =>
    httpClient.delete<RemoveWatcherResponse>(`tracker/watcher/${watcherId}`),
  subscribe: (entityId: number, entityType: EntityType, toTrack: EntityType) =>
    httpClient.put(`tracker/subscribe/${entityId}/${entityType}/${toTrack}`),
  unsubscribeTrackers: (entityIds: number[], entityToUntrack: EntityType) =>
    httpClient.put<RemoveWatcherResponse>(`tracker/unsubscribe`, {
      data: { entityIds, entityToUntrack },
    }),
  summary: () => httpClient.get<TrackingSummary>(`tracker/summary`),
  get: (entityId: number, entityType: EntityType) =>
    httpClient.get<WatcherEntitySummary>(`tracker/${entityId}/${entityType}`),
  getSamples: (
    pointer: number | null,
    entityType: EntityType | null,
    query: SamplesQuery,
  ) =>
    httpClient.get<Sample[]>(`tracker/samples`, {
      params: {
        pointer,
        entityType,
        sortBy: query.sortBy,
        sortDesc: query.sortDesc,
      },
    }),
  getSamplesFromPointer: (
    pointer: number,
    entityType: EntityType,
    query: SamplesQuery,
  ) =>
    httpClient.get<Sample[]>(`tracker/samples/pointer`, {
      params: {
        pointer,
        entityType,
        sortBy: query.sortBy,
        sortDesc: query.sortDesc,
      },
    }),
  getInquiries: (pointer: number | null, entityType: EntityType | null) =>
    httpClient.get<SupplierInquiry[]>(`tracker/inquiries`, {
      params: { pointer, entityType },
    }),
  getInquiriesFromPointer: (pointer: number, entityType: EntityType) =>
    httpClient.get<SupplierInquiry[]>(`tracker/inquiries/pointer`, {
      params: { pointer, entityType },
    }),
  getOffers: (pointer: number | null, entityType: EntityType | null) =>
    httpClient.get<Offer[]>(`tracker/offers`, {
      params: { pointer, entityType },
    }),
};
